/* Fourth Man Roasters — shared cart-render mount.
 *
 * Dawn rebuilds the cart drawer and the cart footer by replacing innerHTML
 * wholesale on every cart mutation, which destroys anything we injected, and it
 * publishes cart events through an in-module pubsub (assets/pubsub.js, names in
 * constants.js) rather than on the document. Document-level 'cart:updated'
 * listeners therefore never fire. That combination made the free-ship nudge
 * invisible at the highest-intent moment (add to cart, drawer opens) until
 * 2026-07-29.
 *
 * HORIZON (ported 2026-10-16). Horizon does NOT replace innerHTML. It MORPHS
 * the cart section in place (assets/morph.js): nodes that survive keep their
 * identity, so a quantity change usually adds no new [data-fmr-referral] node
 * and the observer below would never see it. Horizon instead publishes Shopify
 * standard events that bubble to the document ('shopify:cart:lines-update',
 * '...:note-update', '...:discount-update', plus 'cart-section:restored' after
 * a back/forward cache restore), each carrying a promise that settles when the
 * cart response is in. We re-render after that promise settles.
 *
 * Morph also deletes any child it does not find in the server HTML, i.e. our
 * banners. So the referral field (cart-summary.liquid) and the pickup anchor
 * (templates/product.json) sit inside elements marked data-skip-subtree-update,
 * which tells morph to leave their children alone. Remove that attribute and
 * the banners vanish on the first quantity change.
 *
 * The observer stays: it still catches surfaces that are inserted fresh (the
 * drawer going from empty to filled inserts the whole summary, referral field
 * included).
 *
 * This module owns THE ONE observer and THE ONE debounce for the page, fetches
 * /cart.js once per render cycle, and hands (cart, stats) to every consumer.
 * Consumers must not add their own observers.
 *
 *   window.FMR.onCartRender(function (cart, stats) { ... });
 *   stats = { lbs: <total coffee pounds>, firstOneLb: <first 1 lb line or null> }
 *
 * Consumers must mark injected nodes with the class "fmr-injected" so the
 * observer ignores its own output and cannot feed itself.
 *
 * Never breaks the cart: every failure path is swallowed.
 */
(function () {
  var FMR = (window.FMR = window.FMR || {});
  if (FMR.onCartRender) return; // idempotent if this asset is ever loaded twice

  // Anchors whose (re)appearance means a surface we decorate was re-rendered.
  var ANCHOR_SEL = '[data-fmr-referral],[data-fmr-pickup]';
  var callbacks = [];

  function lbsOf(s) {
    var m = /([\d.]+)\s*lb/i.exec(s || '');
    return m ? Number(m[1]) : 0;
  }

  function coffeeStats(cart) {
    var lbs = 0, firstOneLb = null;
    (cart.items || []).forEach(function (it) {
      var per = lbsOf(it.variant_title) || lbsOf(it.title);
      if (!per) return;
      lbs += per * it.quantity;
      if (per === 1 && !firstOneLb) firstOneLb = it;
    });
    return { lbs: lbs, firstOneLb: firstOneLb };
  }

  function render() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var stats = coffeeStats(cart);
        for (var i = 0; i < callbacks.length; i++) {
          // One misbehaving consumer must not stop the others.
          try { callbacks[i](cart, stats); } catch (e) {}
        }
      })
      .catch(function () { /* never break the cart */ });
  }

  var pending = false;
  function scheduleRender() {
    if (pending) return;
    pending = true;
    setTimeout(function () { pending = false; render(); }, 60);
  }

  FMR.onCartRender = function (cb) {
    if (typeof cb !== 'function') return;
    callbacks.push(cb);
    if (document.readyState !== 'loading') scheduleRender();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRender);
  }
  document.addEventListener('shopify:section:load', scheduleRender);
  // Belt and braces only. Dawn does not dispatch these; see the header note.
  document.addEventListener('cart:refresh', scheduleRender);
  document.addEventListener('cart:updated', scheduleRender);

  // Horizon's cart events (see the header note). Wait for the event's promise,
  // so /cart.js is read after the mutation landed, not before. Resolve or
  // reject, we re-render either way: a failed update still leaves a real cart.
  var CART_EVENTS = [
    'shopify:cart:lines-update',
    'shopify:cart:note-update',
    'shopify:cart:discount-update',
    'cart-section:restored'
  ];
  for (var k = 0; k < CART_EVENTS.length; k++) {
    document.addEventListener(CART_EVENTS[k], function (e) {
      var p = e && e.promise;
      if (p && typeof p.then === 'function') p.then(scheduleRender, scheduleRender);
      else scheduleRender();
    });
  }

  if (window.MutationObserver) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.classList && n.classList.contains('fmr-injected')) continue;
          if (n.matches && (n.matches(ANCHOR_SEL) || n.querySelector(ANCHOR_SEL))) {
            scheduleRender();
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
